import React, { useState, useEffect } from "react";
import Meta from "../Meta";
import PageHeader from "../components/PageHeader";
import Icon from "../components/Icon";
import EmptyState from "../components/EmptyState";
import Loader from "../loader";
import ApiService from "../../../services/ApiService";
import { applyLang } from "../../../i18n";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { resolveImage } from "../utils";

const styles = `
.t26-galerie-images {
  padding: 0 clamp(16px, 4vw, 32px) clamp(40px, 6vw, 80px);
  max-width: var(--container);
  margin-inline: auto;
}
.t26-mosaic {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  grid-auto-rows: 180px;
  gap: 8px;
}
.t26-mosaic__cell {
  position: relative;
  overflow: hidden;
  border-radius: var(--r-md);
  background: color-mix(in oklab, var(--primary) 14%, var(--bg-2));
  cursor: pointer;
  isolation: isolate;
  border: 0;
  padding: 0;
}
.t26-mosaic__cell--tall { grid-row: span 2; }
.t26-mosaic__cell img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform .4s ease;
}
.t26-mosaic__cell:hover img { transform: scale(1.04); }

.t26-galerie-loadmore {
  display: flex; justify-content: center; margin-top: 24px;
}

.t26-lightbox {
  position: fixed; inset: 0;
  background: rgba(8,14,22,.92);
  z-index: 100;
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
}
.t26-lightbox__close {
  position: absolute; top: 20px; inset-inline-end: 20px;
  width: 40px; height: 40px;
  border-radius: 50%;
  background: rgba(255,255,255,.1);
  border: 1px solid rgba(255,255,255,.25);
  color: white;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer;
}
.t26-lightbox__nav {
  position: absolute;
  top: 50%; transform: translateY(-50%);
  width: 48px; height: 48px;
  border-radius: 50%;
  background: rgba(255,255,255,.1);
  border: 1px solid rgba(255,255,255,.25);
  color: white;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer;
}
.t26-lightbox__nav--prev { inset-inline-start: 20px; }
.t26-lightbox__nav--next { inset-inline-end: 20px; }
.t26-lightbox img {
  max-width: 90vw; max-height: 86vh;
  border-radius: var(--r-md);
  box-shadow: 0 20px 60px rgba(0,0,0,.5);
}
`;

/**
 * Page détail d'une galerie.
 * Source : context.data[0] = galerie fetchée côté SSR par le router.
 * Images : GET /galeries/images/{galerieId}/{page} → tableau de strings.
 * Pagination : "Charger plus" tant que l'API renvoie des images.
 */
export default function GalerieImages({ context, loading }) {
    const { org, data, lang } = context;
    const rawGalerie = data?.[0] || null;
    const galerie = rawGalerie ? applyLang(rawGalerie, lang) : null;

    const [images, setImages] = useState([]);
    const [page, setPage] = useState(0);
    const [finished, setFinished] = useState(false);
    const [busy, setBusy] = useState(true);
    const [lightboxIndex, setLightboxIndex] = useState(null);

    const fetchPage = async (init = false) => {
        if (!galerie?.id) { setBusy(false); return; }
        const nextPage = init ? 1 : page + 1;
        setBusy(true);
        try {
            const res = await ApiService.get(`/galeries/images/${galerie.id}/${nextPage}`);
            const list = Array.isArray(res?.data) ? res.data : [];
            setImages(prev => (init ? list : [...prev, ...list]));
            setPage(nextPage);
            setFinished(list.length === 0);
        } catch (e) {
            setFinished(true);
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (galerie?.id) fetchPage(true);
        else setBusy(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [galerie?.id]);

    useEffect(() => {
        if (lightboxIndex == null) return;
        const onKey = (e) => {
            if (e.key === "Escape") setLightboxIndex(null);
            if (e.key === "ArrowLeft") setLightboxIndex(i => (i - 1 + images.length) % images.length);
            if (e.key === "ArrowRight") setLightboxIndex(i => (i + 1) % images.length);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [lightboxIndex, images.length]);

    if (loading) return <Loader />;
    if (!galerie) {
        return (
            <>
                <Meta context={context} title={t`Galerie introuvable`} noindex />
                <div style={{ padding: 60, textAlign: "center", color: "var(--ink-3)" }}>
                    <Trans>Cette galerie n'a pas été trouvée.</Trans>
                </div>
            </>
        );
    }

    const close = () => setLightboxIndex(null);
    const prev = () => setLightboxIndex(i => (i - 1 + images.length) % images.length);
    const next = () => setLightboxIndex(i => (i + 1) % images.length);

    return (
        <>
            <Meta
                context={context}
                title={galerie.name}
                description={galerie.description}
                image={galerie.bgImage ? resolveImage(galerie.bgImage) : null}
            />
            <style dangerouslySetInnerHTML={{ __html: styles }} />

            <PageHeader
                title={galerie.name || t`Galerie`}
                subtitle={galerie.description}
                crumbs={[
                    { label: t`Accueil`, url: org.BASE_WEB_URL },
                    { label: t`Galeries`, url: `${org.BASE_WEB_URL}/galeries` },
                    { label: galerie.name || t`Galerie` },
                ]}
            />

            <div className="t26-galerie-images">
                {!busy && images.length === 0 ? (
                    <EmptyState
                        icon="IconPhotoOff"
                        title={t`Aucune image dans cette galerie`}
                        subtitle={t`Les photos seront ajoutées prochainement.`}
                    />
                ) : (
                    <>
                        <div className="t26-mosaic">
                            {images.map((img, i) => (
                                <button
                                    key={i}
                                    className={`t26-mosaic__cell ${i % 5 === 0 ? "t26-mosaic__cell--tall" : ""}`}
                                    onClick={() => setLightboxIndex(i)}
                                    aria-label={`Image ${i + 1}`}
                                >
                                    <img src={resolveImage(img)} alt="" loading="lazy" />
                                </button>
                            ))}
                        </div>

                        {!finished && (
                            <div className="t26-galerie-loadmore">
                                <button
                                    className="tr-btn tr-btn--ghost tr-btn--md tr-focusable"
                                    onClick={() => fetchPage(false)}
                                    disabled={busy}
                                >
                                    {busy ? <Trans>Chargement…</Trans> : <Trans>Charger plus</Trans>}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {lightboxIndex != null && (
                <div className="t26-lightbox" onClick={close}>
                    <button className="t26-lightbox__close" onClick={(e) => { e.stopPropagation(); close(); }} aria-label={t`Fermer`}>
                        <Icon name="close" size={20} />
                    </button>
                    {images.length > 1 && (
                        <>
                            <button className="t26-lightbox__nav t26-lightbox__nav--prev" onClick={(e) => { e.stopPropagation(); prev(); }} aria-label={t`Précédent`}>
                                <Icon name="chevronRight" size={20} style={{ transform: "scaleX(-1)" }} className="t26-flip-rtl" />
                            </button>
                            <button className="t26-lightbox__nav t26-lightbox__nav--next" onClick={(e) => { e.stopPropagation(); next(); }} aria-label={t`Suivant`}>
                                <Icon name="chevronRight" size={20} className="t26-flip-rtl" />
                            </button>
                        </>
                    )}
                    <img src={resolveImage(images[lightboxIndex])} alt="" onClick={(e) => e.stopPropagation()} />
                </div>
            )}
        </>
    );
}
